<template>
  <van-popup
    :show="modelValue"
    position="bottom"
    round
    safe-area-inset-bottom
    class="account-sheet-popup"
    @update:show="emit('update:modelValue', $event)"
  >
    <section class="account-sheet">
      <header class="account-sheet__header">
        <span class="account-sheet__header-spacer" />
        <h2>{{ t("home.viewAccount") }}</h2>
        <button
          type="button"
          class="account-sheet__close"
          @click="emit('update:modelValue', false)"
        >
          <app-image class="app-cancel-icon" :src="cancelIcon" alt="" />
        </button>
      </header>

      <section class="account-sheet__list">
        <button
          v-for="item in accounts"
          :key="
            item.address ||
            item.ethAddress ||
            item.tronAddress ||
            item.bitcoinAddress
          "
          type="button"
          class="account-sheet__item"
          :class="{ active: isActiveAccount(item) }"
          @click="selectAccount(item)"
        >
          <div class="account-sheet__left">
            <div class="account-sheet__avatar">
              <app-image v-if="avatarUrl(item)" :src="avatarUrl(item)" alt="" />
              <span v-else>{{ avatarText(item.name) }}</span>
            </div>

            <div class="account-sheet__meta">
              <div class="account-sheet__name-row">
                <div data-strong>{{ item.name || "WinPay" }}</div>
                <em v-if="isEmailAccount(item)">{{ t("auth.emailLogin") }}</em>
              </div>

              <div class="account-sheet__address-row">
                <span>{{ shortAddress(displayAddress(item)) }}</span>
                <button
                  type="button"
                  class="account-sheet__copy"
                  @click.stop="copyAddress(displayAddress(item))"
                >
                  <app-image
                    class="account-sheet__copy-icon"
                    :src="copyIcon"
                    alt=""
                  />
                </button>
              </div>
            </div>
          </div>

          <div class="account-sheet__right">
            <div data-strong>{{ itemAssetTotal(item) }} USDT</div>
            <span>{{ itemMainBalance(item) }} {{ itemMainCoin(item) }}</span>
          </div>
        </button>
      </section>

      <footer class="account-sheet__footer">
        <button type="button" class="account-sheet__logout" @click="logout">
          {{ t("mine.logout") }}
        </button>
      </footer>
    </section>
  </van-popup>

  <van-popup
    v-model:show="showLogoutConfirm"
    position="center"
    round
    class="account-logout-popup"
    overlay-class="account-logout-overlay"
    :close-on-click-overlay="false"
  >
    <section class="account-logout-dialog">
      <header class="account-logout-dialog__header">
        <span />
        <h3>{{ t("mine.logoutConfirmTitle") }}</h3>
        <button
          type="button"
          class="account-logout-dialog__close"
          @click="showLogoutConfirm = false"
        >
          <app-image class="app-cancel-icon" :src="cancelIcon" alt="" />
        </button>
      </header>

      <p class="account-logout-dialog__message">
        {{ t("mine.logoutConfirmAccount", { name: currentAccountName }) }}
      </p>

      <div class="account-logout-dialog__actions">
        <button type="button" @click="showLogoutConfirm = false">
          {{ t("common.cancel") }}
        </button>
        <button type="button" @click="confirmLogout">
          {{ t("common.confirm") }}
        </button>
      </div>
    </section>
  </van-popup>
</template>

<script setup>
import { computed, ref } from "vue";
import { showFailToast } from "vant";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import {
  getAnyAccountAddress,
  resolveAccountAddress,
  useProfileStore,
} from "@/stores/profile";
import { resolveAvatarText, resolveAvatarUrl } from "@/utils/avatar";
import { shortAddress, formatMoney, formatTokenAmount } from "@/utils/format";
import { showPassedToast } from "@/utils/toast";
import { copyToClipboard } from "@/utils/clipboard";
import { useWalletTokenStore } from "@/stores/wallet-token";
import cancelIcon from "@/assets/images/cancel_gray.png";
import copyIcon from "@/assets/images/copy_icon_gray_1.svg";

defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["update:modelValue", "selected"]);

const { t } = useI18n();
const router = useRouter();
const profileStore = useProfileStore();
const walletTokenStore = useWalletTokenStore();
const showLogoutConfirm = ref(false);
const accounts = computed(() =>
  profileStore.accounts[0] ? [profileStore.accounts[0]] : [],
);
const currentAccountName = computed(
  () =>
    String(profileStore.currentAccount?.name || "WinPay").trim() || "WinPay",
);
const currentMainCoin = computed(
  () =>
    profileStore.currentNetwork?.coin ||
    profileStore.currentNetwork?.symbol ||
    "ETH",
);
const currentNativeAsset = computed(() => {
  const target = String(currentMainCoin.value || "")
    .trim()
    .toUpperCase();
  return (
    walletTokenStore.assetList.find(
      (item) =>
        String(item?.symbol || item?.coin || "")
          .trim()
          .toUpperCase() === target,
    ) || null
  );
});

function avatarText(name) {
  return resolveAvatarText(name, "W");
}

function avatarUrl(account) {
  return resolveAvatarUrl(account?.avatar);
}

function displayAddress(account) {
  return resolveAccountAddress(account, profileStore.currentNetwork) || "";
}

function isEmailAccount(account) {
  return String(account?.walletWay || "").trim() === "ByEmail";
}

function isActiveAccount(account) {
  const itemKey = String(getAnyAccountAddress(account) || "").toLowerCase();
  const currentKey = String(
    getAnyAccountAddress(profileStore.currentAccount) || "",
  ).toLowerCase();
  return !!itemKey && itemKey === currentKey;
}

function itemAssetTotal(account) {
  const value = Number(
    account?.assetTotal ||
      account?.balanceTo ||
      account?.totalTo ||
      (isActiveAccount(account)
        ? walletTokenStore.assetList.reduce(
            (sum, item) => sum + Number(item?.balanceTo || 0),
            0,
          )
        : 0),
  );
  return formatMoney(value);
}

function itemMainBalance(account) {
  if (isActiveAccount(account) && currentNativeAsset.value) {
    return formatTokenAmount(currentNativeAsset.value.balance);
  }

  const value =
    account?.mainBalance ||
    account?.balance ||
    account?.coinBalance ||
    account?.nativeBalance ||
    0;
  return formatTokenAmount(value);
}

function itemMainCoin(account) {
  return String(account?.mainCoin || currentMainCoin.value || "").trim();
}

async function copyAddress(address) {
  const copied = await copyToClipboard(String(address || ""));
  if (copied) {
    showPassedToast(t("wallet.copySuccess"));
    return;
  }
  showFailToast(t("wallet.copyFailed"));
}

async function selectAccount(account) {
  if (!profileStore.canSupportNetwork(profileStore.currentNetwork, account)) {
    showFailToast(t("home.currentAccountNotSupport"));
    return;
  }
  await profileStore.setCurrentAccount(account);
  emit("selected", account);
  emit("update:modelValue", false);
}

function logout() {
  showLogoutConfirm.value = true;
}

async function confirmLogout() {
  showLogoutConfirm.value = false;
  emit("update:modelValue", false);
  profileStore.clearAuth();
  await router.push("/login");
}
</script>

<style scoped lang="scss">
.account-sheet-popup {
  overflow: hidden;
}

.account-sheet {
  padding: 18px 0 calc(22px + env(safe-area-inset-bottom));
  background: #ffffff;
}

.account-sheet__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 16px 18px;

  h2 {
    margin: 0;
    color: #000000;
    font-size: 18px;
    font-weight: 700;
    line-height: 34px;
  }
}

.account-sheet__header-spacer {
  width: 36px;
  height: 36px;
}

.account-sheet__close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  padding: 0;
  border: 0;
  background: transparent;

  .app-image {
    width: 16px;
    height: 16px;
    object-fit: contain;
  }
}

.account-sheet__list {
  max-height: calc(400px - 70px);
  overflow-y: auto;
}

.account-sheet__footer {
  padding: 14px 16px 0;
  border-top: 1px solid #f0f0f0;
}

.account-sheet__logout {
  width: 100%;
  height: 44px;
  padding: 0 16px;
  border: 0;
  border-radius: 22px;
  background: #f1f1f2;
  color: #333;
  font-weight: 700;
  font-size: 16px;
  line-height: 22px;
}

:global(.account-logout-popup.van-popup--center) {
  width: calc(100% - 40px);
  max-width: 356px;
  overflow: hidden;
  border-radius: 20px;
  background: #ffffff;
}

:global(.account-logout-overlay) {
  width: var(--app-shell-width);
  max-width: 100vw;
  left: var(--app-shell-left);
  right: auto;
}

.account-logout-dialog {
  box-sizing: border-box;
  padding: 20px 18px 24px;
  background: #ffffff;
}

.account-logout-dialog__header {
  display: grid;
  grid-template-columns: 28px minmax(0, 1fr) 28px;
  align-items: center;

  h3 {
    margin: 0;
    color: #000000;
    font-size: 20px;
    font-weight: 500;
    line-height: 28px;
    text-align: center;
  }
}

.account-logout-dialog__close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: 0;
  background: transparent;

  .app-image {
    width: 18px;
    height: 18px;
    object-fit: contain;
  }
}

.account-logout-dialog__message {
  margin: 26px 10px 24px;
  color: #333333;
  font-size: 16px;
  line-height: 24px;
  text-align: left;
}

.account-logout-dialog__actions {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;

  button {
    height: 44px;
    padding: 0 12px;
    border: 0;
    border-radius: 22px;
    font-size: 16px;
    font-weight: 400;
    line-height: 22px;

    &:first-child {
      background: #eaf2ff;
      color: #2667ff;
    }

    &:last-child {
      background: #2667ff;
      color: #ffffff;
    }
  }
}

.account-sheet__item {
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  min-height: 68px;
  padding: 14px 15px;
  border: 0;
  background: #ffffff;
  color: #222222;
  text-align: left;

  &.active {
    background: #eaf2ff;
  }
}

.account-sheet__left {
  display: flex;
  min-width: 0;
  align-items: center;
  flex: 1;
}

.account-sheet__avatar {
  display: flex;
  flex: 0 0 32px;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: linear-gradient(135deg, #4080ff, #14c9c9);
  color: #ffffff;
  font-size: 12px;
  font-weight: 500;

  .app-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 8px;
  }
}

.account-sheet__meta {
  flex: 1;
  min-width: 0;
  margin-left: 8px;
}

.account-sheet__name-row {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 10px;

  [data-strong] {
    min-width: 0;
    color: #222222;
    font-size: 14px;
    font-weight: 500;
    line-height: 22px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  em {
    flex: 0 0 auto;
    padding: 3px 8px;
    border: 1px solid #c3daff;
    border-radius: 8px;
    color: #4072e9;
    font-size: 12px;
    font-style: normal;
    font-weight: 500;
    line-height: 18px;
  }
}

.account-sheet__address-row {
  display: flex;
  min-width: 0;
  align-items: center;
  margin-top: 6px;
  gap: 6px;

  span {
    min-width: 0;
    color: #666666;
    font-size: 14px;
    line-height: 20px;
    white-space: nowrap;
  }
}

.account-sheet__copy {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  // width: 22px;
  // height: 22px;
  padding: 0;
  border: 0;
  background: transparent;

  .app-image {
    width: 14px;
    height: 14px;
    object-fit: contain;
  }
}

.account-sheet__right {
  display: flex;
  flex: 0 0 auto;
  min-width: 108px;
  margin-left: 16px;
  flex-direction: column;
  align-items: flex-end;

  [data-strong] {
    color: #000000;
    font-size: 16px;
    font-weight: 700;
    line-height: 22px;
  }

  span {
    margin-top: 6px;
    color: #666666;
    font-size: 14px;
    line-height: 20px;
  }
}
</style>
